import { z } from "zod";

/**
 * Cede Arena affiliate applications: the public "become an affiliate" form on the
 * arena landing page, and the Solo CMS review surface marketing and the agency
 * work from.
 *
 * Every string here is capped. The submission is unauthenticated and its text
 * reaches a Discord message builder, so an unbounded field is an outbound-message
 * DoS, not just a wide column.
 */

/** Where the applicant's audience is, as they describe it. Not derived from anything. */
export const ARENA_AFFILIATE_REGIONS = [
  "WORLD",
  "APAC",
  "NORTH_AMERICA",
  "EUROPE",
  "LATAM",
  "MENA",
  "AFRICA",
  "CIS",
  "SOUTH_ASIA",
] as const;
export const ArenaAffiliateRegionSchema = z.enum(ARENA_AFFILIATE_REGIONS);
export type ArenaAffiliateRegion = z.infer<typeof ArenaAffiliateRegionSchema>;

/** How they found us — an attribution answer, not a tracked source. */
export const ARENA_AFFILIATE_SOURCES = [
  "FRIEND_OR_CREATOR",
  "CEDE_REACHED_OUT",
  "X",
  "YOUTUBE",
  "DISCORD_OR_TELEGRAM",
  "GOOGLE",
  "EVENT",
  "OTHER",
] as const;
export const ArenaAffiliateSourceSchema = z.enum(ARENA_AFFILIATE_SOURCES);
export type ArenaAffiliateSource = z.infer<typeof ArenaAffiliateSourceSchema>;

/** Which track they apply for. A manager recruits creators rather than promoting to their own audience. */
export const ARENA_AFFILIATE_APPLICATION_TYPES = ["CREATOR", "MANAGER"] as const;
export const ArenaAffiliateApplicationTypeSchema = z.enum(ARENA_AFFILIATE_APPLICATION_TYPES);
export type ArenaAffiliateApplicationType = z.infer<typeof ArenaAffiliateApplicationTypeSchema>;

/** Review state. `NEW` until a human touches it; the other three are terminal-ish and set by hand. */
export const ARENA_AFFILIATE_APPLICATION_STATUSES = ["NEW", "CONTACTED", "SIGNED", "REJECTED"] as const;
export const ArenaAffiliateApplicationStatusSchema = z.enum(ARENA_AFFILIATE_APPLICATION_STATUSES);
export type ArenaAffiliateApplicationStatus = z.infer<typeof ArenaAffiliateApplicationStatusSchema>;

export const ARENA_AFFILIATE_SOCIAL_MAX = 300;

/** The channels the form asks for. Order is the form's display order. */
export const ARENA_AFFILIATE_SOCIAL_KEYS = [
  "website",
  "x",
  "youtube",
  "instagram",
  "discord",
  "facebook",
  "twitch",
  "kick",
] as const;
export type ArenaAffiliateSocialKey = (typeof ARENA_AFFILIATE_SOCIAL_KEYS)[number];

/* A handle is as good as a link here: creators give "@name" as often as a URL,
   and a reviewer opens either one by hand. Kept as free text, capped; the CMS
   linkifies only what parses as http(s) and renders the rest as plain text. */
const OptionalSocialHandleSchema = z
  .string()
  .trim()
  .max(ARENA_AFFILIATE_SOCIAL_MAX)
  .optional()
  .transform((value) => (value === undefined || value === "" ? undefined : value))
  // Re-optional after the transform, or every key becomes required on the object.
  .pipe(z.string().max(ARENA_AFFILIATE_SOCIAL_MAX).optional());

export const ArenaAffiliateSocialsSchema = z
  .object({
    website: OptionalSocialHandleSchema,
    x: OptionalSocialHandleSchema,
    youtube: OptionalSocialHandleSchema,
    instagram: OptionalSocialHandleSchema,
    discord: OptionalSocialHandleSchema,
    facebook: OptionalSocialHandleSchema,
    twitch: OptionalSocialHandleSchema,
    kick: OptionalSocialHandleSchema,
  })
  // An applicant with no reachable audience is not reviewable — there is nothing
  // for marketing to look at, so it is rejected at the boundary rather than stored.
  .refine((socials) => ARENA_AFFILIATE_SOCIAL_KEYS.some((key) => socials[key] !== undefined), {
    error: "at least one social link is required",
  });
export type ArenaAffiliateSocials = z.infer<typeof ArenaAffiliateSocialsSchema>;

export const ARENA_AFFILIATE_DEAL_NOTES_MAX = 1000;
export const ARENA_AFFILIATE_REVIEW_NOTES_MAX = 2000;

const blankToUndefined = (value: string | undefined): string | undefined =>
  value === undefined || value === "" ? undefined : value;

/**
 * Body for `POST /v1/arena-affiliate-applications/submit`.
 *
 * Unauthenticated. `country` is the audience country the applicant declares — it
 * is not, and must not be confused with, the edge-resolved `reportedCountry` the
 * server stamps.
 */
export const SubmitArenaAffiliateApplicationRequestSchema = z.object({
  /** Legal name or the callsign they publish under. */
  name: z.string().trim().min(1).max(80),
  email: z.email().max(254),
  telegram: z.string().trim().max(64).optional().transform(blankToUndefined),
  /** ISO 3166-1 alpha-2, self-declared. Case-normalized so `fr` and `FR` store alike. */
  country: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, { error: "must be an ISO 3166-1 alpha-2 country code" })
    .transform((value) => value.toUpperCase()),
  regionCommunities: ArenaAffiliateRegionSchema.array().min(1).max(ARENA_AFFILIATE_REGIONS.length),
  socials: ArenaAffiliateSocialsSchema,
  heardAboutUs: ArenaAffiliateSourceSchema,
  /** The creator page predates the choice and a cached copy can still post without it; that page only takes creators. */
  applicationType: ArenaAffiliateApplicationTypeSchema.default("CREATOR"),
  /** Free text: what they want out of the deal. */
  dealNotes: z.string().trim().max(ARENA_AFFILIATE_DEAL_NOTES_MAX).optional().transform(blankToUndefined),
  /**
   * Honeypot. Hidden from real users by the form, so any value at all marks the
   * submission as automated. Accepted and capped rather than rejected by schema:
   * the endpoint must answer a bot exactly as it answers a person.
   */
  company: z.string().max(100).optional(),
});
export type SubmitArenaAffiliateApplicationRequest = z.infer<typeof SubmitArenaAffiliateApplicationRequestSchema>;

/** One application as the CMS sees it. */
export const ArenaAffiliateApplicationDtoSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  email: z.string(),
  telegram: z.string().nullable(),
  country: z.string(),
  regionCommunities: ArenaAffiliateRegionSchema.array(),
  socials: ArenaAffiliateSocialsSchema,
  heardAboutUs: ArenaAffiliateSourceSchema,
  applicationType: ArenaAffiliateApplicationTypeSchema,
  dealNotes: z.string().nullable(),
  status: ArenaAffiliateApplicationStatusSchema,
  reviewNotes: z.string().nullable(),
  /** ISO instant of the last review save; null while the row has never been reviewed. */
  reviewedAt: z.string().nullable(),
  /**
   * Country the landing page's edge resolved for the visitor, forwarded to the
   * API. ADVISORY ONLY: the submit route is unauthenticated, so a direct caller
   * can set it to anything. It informs a reviewer; it gates nothing.
   */
  reportedCountry: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ArenaAffiliateApplicationDto = z.infer<typeof ArenaAffiliateApplicationDtoSchema>;

/** Paginated response for `GET /v1/cms/arena-affiliate-applications/get-by-page`. Page is 0-indexed. */
export const ListArenaAffiliateApplicationsResponseSchema = z.object({
  items: ArenaAffiliateApplicationDtoSchema.array(),
  total: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
});
export type ListArenaAffiliateApplicationsResponse = z.infer<typeof ListArenaAffiliateApplicationsResponseSchema>;

/** Body for `POST /v1/cms/arena-affiliate-applications/save-review`. */
export const SaveArenaAffiliateApplicationReviewBodySchema = z.object({
  id: z.number().int().positive(),
  status: ArenaAffiliateApplicationStatusSchema,
  reviewNotes: z.string().trim().max(ARENA_AFFILIATE_REVIEW_NOTES_MAX).optional().transform(blankToUndefined),
});
export type SaveArenaAffiliateApplicationReviewBody = z.infer<typeof SaveArenaAffiliateApplicationReviewBodySchema>;
